import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Scale, Truck, Users, Save, Loader2, Info, Wallet } from "lucide-react";
import { API } from "@/lib/api";
import { SupplierPaymentDialog } from "@/components/SupplierPaymentDialog";

const formatCurrency = (amount) =>
  `\u20B9${Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const PartyTable = ({ parties, drafts, onChange, onSave, savingId, emptyHint, onPay }) => {
  if (!parties || parties.length === 0) {
    return <p className="text-sm text-gray-500 py-6 text-center" data-testid="ob-empty">{emptyHint}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-gray-500">
            <th className="py-2 px-3">Name</th>
            <th className="py-2 px-3 w-48 text-right">Opening Balance (₹)</th>
            <th className="py-2 px-3 w-32 text-right">Saved</th>
            <th className="py-2 px-3 w-44"></th>
          </tr>
        </thead>
        <tbody>
          {parties.map((p) => {
            const draftVal = drafts[p.id] !== undefined ? drafts[p.id] : String(p.opening_balance ?? 0);
            const dirty = String(draftVal) !== String(p.opening_balance ?? 0);
            return (
              <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`ob-row-${p.id}`}>
                <td className="py-2 px-3 font-medium text-gray-800">{p.name}</td>
                <td className="py-2 px-3 text-right">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={draftVal}
                    onChange={(e) => onChange(p.id, e.target.value)}
                    className="text-right"
                    data-testid={`ob-input-${p.id}`}
                  />
                </td>
                <td className="py-2 px-3 text-right text-gray-600" data-testid={`ob-saved-${p.id}`}>
                  {formatCurrency(p.opening_balance)}
                </td>
                <td className="py-2 px-3 text-right">
                  <div className="flex justify-end gap-2">
                    {onPay && Number(p.opening_balance || 0) > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-700 border-green-600 hover:bg-green-50"
                        onClick={() => onPay(p)}
                        data-testid={`ob-pay-${p.id}`}
                      >
                        <Wallet className="h-4 w-4 mr-1" /> Pay
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={dirty ? "default" : "outline"}
                      disabled={!dirty || savingId === p.id}
                      onClick={() => onSave(p)}
                      data-testid={`ob-save-${p.id}`}
                    >
                      {savingId === p.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><Save className="h-4 w-4 mr-1" /> Save</>
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const OpeningBalancesSection = () => {
  const [data, setData] = useState({ suppliers: [], customers: [], default_as_of_date: "2026-06-01" });
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [asOfDate, setAsOfDate] = useState("2026-06-01");
  const [payParty, setPayParty] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/opening-balances/parties`);
        if (!active) return;
        setData(res.data);
        setAsOfDate(res.data.default_as_of_date || "2026-06-01");
        setDrafts({});
      } catch (e) {
        console.error("Error loading opening balances:", e);
        if (active) toast.error("Failed to load opening balances");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/opening-balances/parties`);
      setData(res.data);
      setAsOfDate(res.data.default_as_of_date || "2026-06-01");
      setDrafts({});
    } catch (e) {
      console.error("Error loading opening balances:", e);
      toast.error("Failed to load opening balances");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (id, value) => {
    setDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = async (party) => {
    setSavingId(party.id);
    try {
      const amount = parseFloat(drafts[party.id] ?? party.opening_balance ?? 0) || 0;
      await axios.post(`${API}/opening-balances`, {
        party_type: party.party_type,
        party_id: party.id,
        party_name: party.name,
        amount,
        as_of_date: asOfDate,
      });
      toast.success(`Opening balance saved for ${party.name}`);
      await load();
    } catch (e) {
      console.error("Error saving opening balance:", e);
      toast.error("Failed to save opening balance");
    } finally {
      setSavingId(null);
    }
  };

  const supplierTotal = data.suppliers.reduce((s, p) => s + Number(p.opening_balance || 0), 0);
  const customerTotal = data.customers.reduce((s, p) => s + Number(p.opening_balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="opening-balances-section">
      <div className="flex items-center gap-3">
        <Scale className="h-7 w-7 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Opening Balances</h1>
          <p className="text-gray-600">Carry forward old balances when starting the new accounting period</p>
        </div>
      </div>

      <Card className="border-blue-100 bg-blue-50/50">
        <CardContent className="py-4 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-2 text-sm text-blue-900 flex-1">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Enter the balance carried forward as of the cut-over date. <strong>Supplier</strong> = amount you still owe them;
              <strong> Customer</strong> = amount they still owe you. These are <strong>excluded from P&amp;L / revenue / expenses</strong> and
              only seed the Opening Balance row in each party&apos;s statement.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="as-of-date" className="whitespace-nowrap text-sm">Balance as on</Label>
            <Input
              id="as-of-date"
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="w-40"
              data-testid="ob-as-of-date"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <Tabs defaultValue="suppliers">
          <TabsList>
            <TabsTrigger value="suppliers" data-testid="ob-tab-suppliers">
              <Truck className="h-4 w-4 mr-1" /> Suppliers ({data.suppliers.length})
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="ob-tab-customers">
              <Users className="h-4 w-4 mr-1" /> Customers ({data.customers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="suppliers">
            <Card>
              <CardHeader>
                <CardTitle>Supplier Opening Balances (Payables)</CardTitle>
                <CardDescription>
                  Total carried forward: <strong>{formatCurrency(supplierTotal)}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PartyTable
                  parties={data.suppliers}
                  drafts={drafts}
                  onChange={handleChange}
                  onSave={handleSave}
                  savingId={savingId}
                  onPay={setPayParty}
                  emptyHint="No suppliers found. Add suppliers from the Suppliers page first."
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="customers">
            <Card>
              <CardHeader>
                <CardTitle>Customer Opening Balances (Receivables)</CardTitle>
                <CardDescription>
                  Total carried forward: <strong>{formatCurrency(customerTotal)}</strong>. Customers shown here are your credit
                  (account) customers — add them via Accounting → Credit Management → Add Credit Account.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PartyTable
                  parties={data.customers}
                  drafts={drafts}
                  onChange={handleChange}
                  onSave={handleSave}
                  savingId={savingId}
                  emptyHint="No customer credit accounts found. Create one in Accounting → Credit Management."
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {payParty && (
        <SupplierPaymentDialog
          open={!!payParty}
          onOpenChange={(o) => { if (!o) setPayParty(null); }}
          supplierName={payParty.name}
          owedAmount={Number(payParty.opening_balance || 0)}
          basePayload={{ supplier_id: payParty.id, supplier_name: payParty.name, target: "opening_balance" }}
          onPaid={load}
        />
      )}
    </div>
  );
};

export default OpeningBalancesSection;
